/* ==========================================================================
   Rook UI Core — Collage Group Controller
   Web Component: <rk-collage-group-element>

   Animação de entrada "burst": cada cartão parte do centro da colagem
   (opacidade 0, escala reduzida, deslocado até o centro do grid) e anima até
   sua posição final quando a seção entra na viewport, com cascata entre
   cartões. Segue o mesmo padrão do rk-reveal (IntersectionObserver,
   prefers-reduced-motion, hydration eager para evitar flash).
   ========================================================================== */

import { RkComponent } from '@rook/component';

const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

class RkCollageGroupElement extends RkComponent {
    static hydration = 'eager';

    #observer = null;
    #grid = null;
    #items = [];

    setup() {
        this._duration = parseInt(this.dataset.duration, 10) || 700;
        this._stagger = parseInt(this.dataset.stagger, 10) || 90;
        this._threshold = parseFloat(this.dataset.threshold) || 0.15;

        this.#grid = this.querySelector('.rk-collage-group__grid');
        this.#items = this.#grid ? Array.from(this.#grid.children) : [];

        if (!this.#items.length) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        this.#applyHiddenState();

        // O limiar vale para a parte da TELA ocupada pela colagem: uma colagem mais alta que a
        // viewport (celular) nunca fica 50% visível, e as fotos ficavam escondidas para sempre.
        const steps = Array.from({ length: 21 }, (_, i) => i / 20);
        this.#observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const needed = Math.min(entry.boundingClientRect.height, window.innerHeight) * this._threshold;
                    if (entry.isIntersecting && entry.intersectionRect.height >= needed) {
                        this.#reveal();
                        this.#observer.unobserve(this);
                    }
                });
            },
            { threshold: steps }
        );

        this.#observer.observe(this);
    }

    teardown() {
        this.#observer?.disconnect();
        this.#observer = null;
    }

    #centerDelta(item) {
        const gridRect = this.#grid.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        const dx = Math.round(
            (gridRect.left + gridRect.width / 2) - (itemRect.left + itemRect.width / 2)
        );
        const dy = Math.round(
            (gridRect.top + gridRect.height / 2) - (itemRect.top + itemRect.height / 2)
        );

        return { dx, dy };
    }

    #applyHiddenState() {
        this.#items.forEach((item) => {
            const { dx, dy } = this.#centerDelta(item);
            item.style.transition = 'none';
            item.style.opacity = '0';
            item.style.transform = `translate(${dx}px, ${dy}px) scale(0.4)`;
            item.style.willChange = 'opacity, transform';
        });
    }

    #reveal() {
        // Re-measure right before animating so late layout shifts (webfonts,
        // image aspect-ratio settling) don't leave the burst vector stale.
        this.#applyHiddenState();

        this.#items.forEach((item, index) => {
            const delay = this._stagger * index;
            requestAnimationFrame(() => {
                item.style.transition = `opacity ${this._duration}ms ${EASING} ${delay}ms, transform ${this._duration}ms ${EASING} ${delay}ms`;
                item.style.opacity = '1';
                item.style.transform = 'translate(0, 0) scale(1)';
            });
        });

        this.classList.add('rk-collage-group--visible');
        this.rkEmit('collage:visible', {});
    }
}

if (!customElements.get('rk-collage-group-element')) {
    customElements.define('rk-collage-group-element', RkCollageGroupElement);
}
